import JSON5 from 'json5';
import ShopifyAPI from '@/gateway/ShopifyAPI';
import { parseGoalsFromHtmlElement } from '@/utils/goals-map';
import { parseFragrancesFromHtmlElement } from '@/utils/fragrance-map';
import {
  getColorsFromVariants,
  getColorNameFromVariantId
} from '@/utils/colors-map';
import { VariantSelectionContext } from '@/components/mini-quiz/mini-quiz-basic/variantsHandler';
import { fireGTMEvent } from '@/utils';

const UPDATING_STATE_TEXT = 'Updating...';
const CLICK_EDIT_CART_EVENT = 'dl_ga4_click_edit_cart';
const CUSTOMIZATIONS_UPDATED_EVENT = 'dl_ga4_customizations_updated';
const CLICK_RATE_LIMIT_MS = 2000; // ms

export default class CartContext extends HTMLElement {
  selectLineItem(lineItemKey) {
    return this.lineItems.find((item) => item.key == lineItemKey);
  }

  selectLineItemProperties(lineItemKey) {
    const lineItem = this.selectLineItem(lineItemKey);
    const properties = lineItem.properties;

    return {
      name: properties['__profile_name'],
      goals: properties['__hair_goals'],
      hairFragrance:
        properties['__profile_type'] === 'hair'
          ? properties['__fragrance']
          : '',
      bodyFragrance:
        properties['__profile_type'] === 'body'
          ? properties['__fragrance']
          : '',
      limitedFragrances: properties['__product_handle']?.includes('sos')
        ? '1'
        : '0',
      productTitle: lineItem.product_title,
      productHandle: properties['__product_handle'] ?? '',
      profileType: properties['__profile_type'],
      bodyMoisture: properties['__body_moisture'] ?? '',
      colors: getColorsFromVariants(lineItem.variants),
      colorName: getColorNameFromVariantId(lineItem.id, lineItem.variants),
      hairType: properties['__hair_type'] ?? ''
    };
  }

  get lineItems() {
    return JSON5.parse(this.getAttribute('line-items').trim());
  }

  get customizationDetails() {
    return document.querySelector('customization-details');
  }

  get customizationModal() {
    return document.querySelector('#customization-details-modal');
  }

  get editLineItemBtns() {
    return document.querySelectorAll('.edit-line-item-btn');
  }

  get customizationCloseBtn() {
    return this.customizationModal?.querySelector('.close-btn');
  }

  get currentUser() {
    return {
      currency: {
        user_name: window.marketingUserData.name || '',
        user_email: window.marketingUserData.email || ''
      }
    };
  }

  populateCustomizationDetails({ lineItemKey }) {
    const lineItemProperties = this.selectLineItemProperties(lineItemKey);
    this.customizationDetails.setAttribute(
      'name',
      lineItemProperties.name?.substring(3) || ''
    );
    this.customizationDetails.setAttribute('goals', lineItemProperties.goals);
    this.customizationDetails.setAttribute(
      'hair-fragrance',
      lineItemProperties.hairFragrance
    );
    this.customizationDetails.setAttribute(
      'body-fragrance',
      lineItemProperties.bodyFragrance
    );
    this.customizationDetails.setAttribute(
      'profile-type',
      lineItemProperties.profileType
    );
    this.customizationDetails.setAttribute(
      'body-moisture',
      lineItemProperties.bodyMoisture
    );
    this.customizationDetails.setAttribute(
      'limited-fragrances',
      lineItemProperties.limitedFragrances
    );
    this.customizationDetails.setAttribute(
      'colors',
      JSON.stringify(lineItemProperties.colors)
    );
    this.customizationDetails.setAttribute(
      'preselected-color',
      lineItemProperties.colorName
    );
    this.customizationDetails.setAttribute(
      'product-handle',
      lineItemProperties.productHandle
    );
    this.customizationDetails.setAttribute(
      'product-title',
      lineItemProperties.productTitle
    );
    this.customizationDetails.setAttribute('line-item-key', lineItemKey);
    this.customizationDetails.setAttribute(
      'hair-type',
      lineItemProperties.hairType
    );

    this.customizationDetails.shadowRoot.querySelector(
      'color-selection'
    ).onSelected = (color) => {
      this.customizationDetails.shadowRoot.querySelector(
        '#new-color-selection'
      ).innerText = color;
    };
  }

  async updateCartContent() {
    const mainContent = document.querySelector('#MainContent');
    const mainSection = mainContent.firstElementChild;
    const maindIdFormatted = mainSection.id.replace('shopify-section-', '');
    await ShopifyAPI.updateSection(maindIdFormatted);
  }

  async submitCustomizations({ lineItemKey }) {
    this.customizationDetails.shadowRoot.querySelector(
      '#update-formula'
    ).innerText = UPDATING_STATE_TEXT;

    const lineItem = this.selectLineItem(lineItemKey);
    let hairFragrance = '';
    let bodyFragrance = '';

    const hairFragranceSelector =
      this.customizationDetails.shadowRoot.querySelector(
        '#hair-fragrance-selector'
      );

    if (hairFragranceSelector) {
      hairFragrance = JSON.parse(
        hairFragranceSelector.getAttribute('selection')
      )?.dataKey;
    }

    const bodyFragranceSelector =
      this.customizationDetails.shadowRoot.querySelector(
        '#body-fragrance-selector'
      );
    if (bodyFragranceSelector) {
      bodyFragrance = JSON.parse(
        bodyFragranceSelector.getAttribute('selection')
      )?.dataKey;
    }

    let goals;
    const goalsSelector =
      this.customizationDetails.shadowRoot.querySelector('#goals-selector');
    if (goalsSelector) {
      goals = JSON.parse(goalsSelector.getAttribute('selection'))
        .map((goal) => goal?.dataKey)
        .join(',');
    }

    const profileName = this.customizationDetails.shadowRoot.querySelector(
      '#profile-name'
    ).value
      ? `of ${
          this.customizationDetails.shadowRoot.querySelector('#profile-name')
            .value
        }`
      : 'of beauty';

    let bodyMoisture;
    const bodyMoistureSelector =
      this.customizationDetails.shadowRoot.querySelector(
        '#body-moisture-selector'
      );
    if (bodyMoistureSelector) {
      bodyMoisture = JSON.parse(
        bodyMoistureSelector.getAttribute('selection')
      ).dataKey;
    }

    let objectFragrance;
    if (lineItem.properties.__profile_type === 'hair') {
      objectFragrance = hairFragrance;
    } else if (lineItem.properties.__profile_type === 'body') {
      objectFragrance = bodyFragrance;
    }

    const colorSelected =
      this.customizationDetails.shadowRoot.querySelector(
        'color-selection'
      )?.selectedColor;
    const variantContext = new VariantSelectionContext();
    const newVariantId = variantContext.selectVariant(
      { variants: lineItem.variants },
      { colorSelected }
    );
    const variantId = newVariantId || lineItem.id;
    const variantUpdated = newVariantId && newVariantId !== lineItem.id;

    const isHairProfile = ['hair', 'mixed'].includes(
      lineItem.properties?.__profile_type
    );
    const isBodyProfile = ['body', 'mixed'].includes(
      lineItem.properties?.__profile_type
    );

    const payload = {
      variant_id: variantId,
      properties: {
        ...lineItem.properties,
        __profile_name: profileName,
        __fragrance: objectFragrance,
        ...(isHairProfile && { __hair_goals: goals }),
        ...(isBodyProfile && { __body_moisture: bodyMoisture })
      }
    };

    try {
      if (variantUpdated) {
        await ShopifyAPI.replaceItemWithNewVariant(
          lineItemKey,
          variantId,
          payload
        );
      } else {
        await ShopifyAPI.changeCart({
          id: `${lineItemKey}`,
          ...payload
        });
      }
    } catch (error) {
      console.log('error =>', error);
    } finally {
      const goalsSorted = lineItem.properties?.__hair_goals
        ?.split(',')
        .sort()
        .join(',');
      const goalsSortedNew = payload.properties?.__hair_goals
        ?.split(',')
        .sort()
        .join(',');
      const customizationChanges = this.getCustomizationChanges({
        properties: {
          ...lineItem.properties,
          __hair_goals: goalsSorted || '',
          __color: this.customizationDetails.getAttribute('preselected-color')
        },
        newProperties: {
          ...payload.properties,
          __hair_goals: goalsSortedNew || '',
          __color: colorSelected
        }
      });

      const eventData = {
        ...this.currentUser,
        customizationChanges
      };

      fireGTMEvent(CUSTOMIZATIONS_UPDATED_EVENT, eventData);

      await this.updateCartContent();
      this.customizationModal.enabled = false;
    }
  }

  getCustomizationChanges({ properties, newProperties }) {
    const customizationChanges = {};
    const propertiesToCompare = [
      'hair_goals',
      'body_moisture',
      'fragrance',
      'profile_name',
      'color'
    ];

    propertiesToCompare.forEach((propertyName) => {
      const value = properties[`__${propertyName}`];
      const newValue = newProperties[`__${propertyName}`];

      if (value && value !== newValue) {
        customizationChanges[propertyName] = {
          from: value,
          to: newValue
        };
      }
    });

    return customizationChanges;
  }

  setupEventListeners() {
    this.customizationCloseBtn.addEventListener(
      'click',
      function (e) {
        this.customizationModal.enabled = false;
      }.bind(this)
    );

    let lastClickTime = 0;
    this.editLineItemBtns?.forEach((btn) => {
      btn.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          this.customizationModal.enabled = true;
          const lineItemKey = e.target.dataset.lineItemKey;
          this.populateCustomizationDetails({ lineItemKey });

          const now = Date.now();
          if (now - lastClickTime > CLICK_RATE_LIMIT_MS) {
            fireGTMEvent(CLICK_EDIT_CART_EVENT, this.currentUser);
            lastClickTime = now;
          }
        }.bind(this)
      );
    });
  }

  setupHandlers() {
    this.customizationDetails.handleSubmit =
      this.submitCustomizations.bind(this);
    this.customizationDetails.handleRevertCustomizations =
      this.populateCustomizationDetails.bind(this);
  }

  connectedCallback() {
    this.setupHandlers();
    this.setupEventListeners();
    parseGoalsFromHtmlElement('.product-property-goals');
    parseFragrancesFromHtmlElement('.product-property-fragrance');
  }
}
